import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import AccordionPanel from '@/components/UI/AccordionPanel/AccordionPanel';
import EntryButtons from '@/components/UI/ButtonGroup/ButtonGroup';
import ConfirmDialog from '@/components/UI/Dialog/ConfirmDialog';
import { IcoButtonSimple } from '@/components/UI/IcoButton/IcoButton';
import InfoMsg from '@/components/UI/InfoMsg/InfoMsg';
import LabelDisabledByPDRS from '@/components/UI/LabelDisabledByPDRS/LabelDisabledByPDRS';
import SimpleButton from '@/components/UI/SimpleButton/SimpleButton';
import { CENTER_VIEWER_CR, HELP_AREA, statusCampaign } from '@/helpers/constants';
import { isFieldDisabledBYPRRD } from '@/helpers/utils';
import useGlobalSpinnerContext from '@/hooks/useGlobalSpinner';
import useToastContext from '@/hooks/useToastContext';
import {
  setAffectedVehiclesSeries, setOriginalValues, setCampaignVehiclesSelectedAffectedVehicles,
  setCampaignInfo, setAffectedPlants
} from '@/redux/Campaign/campaign.action';
import {
  getCampaignVehicles, getSelectedPlants, getVolumeHeaderPlants, getCampaignVehiclesVolumes, getOriginalValues,
  getCampaignInfo
} from '@/redux/Campaign/campaign.reducer';
import { setAreaHelp } from '@/redux/Help/help.action';
import { getWorkflow } from '@/redux/Workflow/workflow.reducer';
import affectedVehicleServices from '@/services/affectedVehicleService';
import { AffectedVehicles, CampaignInfo, LimitationSerie, ObjLimitationPlant, ObjLimitationSerie, OriginalValues, Plant, Volume, WKZNamePlant, Workflow } from '@/types';
import SeriesFisrtStepSelector from './SeriesFisrtStepSelector/SeriesFisrtStepSelector';
import styles from './SeriesPanel.module.scss';
import { isViaModelSeries, plantsHaveFiles, mapPlantsWithfiles } from './SeriesRow/series-row-utils';
import SeriesTable from './SeriesTable/SeriesTable';

type SeriesPanelProps = {
  series: ObjLimitationSerie[];
  plantsSelected: ObjLimitationPlant[];
  affectedVehicles: AffectedVehicles;
  headerPlants: WKZNamePlant[];
  volumes: Volume[];
  originalValues: OriginalValues;
  campaignStatus: string;
  userRole: string;
  workflow: Workflow;
  setSeries: (series: ObjLimitationSerie[]) => void;
  setOriginValues: (originalValues: OriginalValues) => void;
  setSelectedAffectedVehicles: (selected: string) => void;
  setPlants: (plants: ObjLimitationPlant[]) => void;
  setHelpArea: (helpArea: string) => void;
  checkEnabled: boolean;
  invalid: boolean;
  campaignInfo: CampaignInfo;
};

const SeriesPanel: React.FC<SeriesPanelProps> = (props) => {
  const { t } = useTranslation();
  const { series, setSeries, setHelpArea, plantsSelected, setPlants, affectedVehicles, invalid, setSelectedAffectedVehicles,
    checkEnabled, headerPlants, volumes, originalValues, setOriginValues, campaignStatus, userRole, workflow, campaignInfo } = props;
  const [modelSeries, setModelSeries] = useState([]);
  const [plantsOptions, setPlantsOptions] = useState<{ plants: WKZNamePlant[] }>({ plants: [] });
  const isSeriesSelected = isViaModelSeries(affectedVehicles);
  const { addToast } = useToastContext();
  const { setLoading } = useGlobalSpinnerContext();
  const [dialogProps, setDialogProps] = useState<
    {
      open: boolean;
      plantsWithFiles?: WKZNamePlant[];
      data?: ObjLimitationPlant[] | ObjLimitationSerie[];
      isPlantsOption?: boolean;
      isMSDeletion?: boolean;
      isPlantsDeletion?: boolean;
    }
  >({
    open: false,
    plantsWithFiles: [],
  });
  const [cantEditPopup, setCantEditPopup] = useState(false);
  const [showFirstStepAdd, setShowFirstStepAdd] = useState(false);

  const onSetOriginValues = (changesIn) => {
    const values = {
      ...originalValues,
      hasChange: true,
      changesIn: {
        ...originalValues.changesIn,
        [changesIn]: true,
      },
    };
    setOriginValues(values);
  };

  const isCenterViewer = userRole === CENTER_VIEWER_CR;

  useEffect(() => {
    Promise.all([
      affectedVehicleServices?.getOnlyPlantsOptions(true),
      affectedVehicleServices?.getModelSeries(true),
    ]).then((res) => {
      const [receivedPlants, receivedSeries] = res;
      setModelSeries(receivedSeries);
      setPlantsOptions(receivedPlants);
    }).catch((error) => addToast(error.message, 'ERROR'));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const checkPlantsFilesAndSave = (newRows: ObjLimitationPlant[] | ObjLimitationSerie[], isPlantsOption?: boolean) => {
    const plantsWithFiles = plantsHaveFiles(newRows, volumes, isPlantsOption ? plantsSelected : headerPlants, isPlantsOption);

    if (plantsWithFiles.length > 0) {
      setDialogProps({ open: true, plantsWithFiles, isPlantsOption, data: newRows });

      return;
    }

    if (isPlantsOption) {
      setPlants(newRows as ObjLimitationPlant[]);
    } else {
      setSeries(newRows as ObjLimitationSerie[]);
    }
    onSetOriginValues(isPlantsOption ? 'plants' : 'series');
    setDialogProps({ open: false });
  };

  const confirmDeletePlants = (isPlantsOption: boolean) => {
    if (dialogProps.data) {
      if (isPlantsOption) setPlants(dialogProps.data as ObjLimitationPlant[]);
      else setSeries(dialogProps.data as ObjLimitationSerie[]);
    }
    onSetOriginValues(isPlantsOption ? 'plants' : 'series');
    setDialogProps({ ...dialogProps, open: false });
  };

  const handleDelete = (itemToDelete?: string, isSeries?: boolean) => {
    const noChecked = isSeries
      ? series.filter((serie) => (itemToDelete ? itemToDelete !== serie.serie : !serie.checked))
      : plantsSelected.filter((plant) => (itemToDelete ? itemToDelete !== plant.wkzNumber : !plant.checked));
    setDialogProps({ open: true, data: noChecked, isMSDeletion: isSeries, isPlantsDeletion: !isSeries });
  };

  const handleNewRowSeries = () => {
    const firstTime = series.length === 0;
    const noEmptyRow = series.filter(({ serie }) => !serie).length === 0;

    if (firstTime) {
      setShowFirstStepAdd(!showFirstStepAdd);
    } else if (noEmptyRow) {
      setSeries([
        ...series,
        {
          serie: '',
          typeVersions: {},
          checked: false,
        }]);
    }
  };

  const handleAddMultipleSeries = async (selectedSeries: string[]) => {
    setLoading(true);

    let plants = await affectedVehicleServices?.gePlantsBySerieList(selectedSeries?.map((serie) => serie?.toUpperCase()));
    plants = plants?.series;

    const newSeries = selectedSeries?.map((serie) => ({ serie, typeVersions: plants?.[serie.toUpperCase()]?.values, checked: false }));
    onSetOriginValues('series');
    setSeries([...series, ...newSeries]);
    setShowFirstStepAdd(false);
    setLoading(false);
  };

  const handleNewRowPlants = () => {
    const firstTime = plantsSelected?.length === 0;
    const noEmptyRow = plantsSelected?.filter(({ plantName }) => !plantName).length === 0;

    if (firstTime) {
      setShowFirstStepAdd(!showFirstStepAdd);
    } else if (noEmptyRow) {
      setPlants([
        ...plantsSelected,
        {
          plantName: '',
          wkzNumber: '',
          affectedSeries: {},
          checked: false,
        }]);
    }
  };

  const handleAddMultiplePlants = async (selectedPlants: Plant[]) => {
    setLoading(true);
    const wkzNumbers = selectedPlants.map((plant) => plant.wkzNumber);
    let modelseries = await affectedVehicleServices.getSeriesByPlantList(wkzNumbers);
    modelseries = modelseries?.seriesByPlant;
    const newPlants = selectedPlants.map((plant) => ({
      ...plant,
      checked: false,
      plantName: plant.plantName,
      affectedSeries: modelseries?.[plant.wkzNumber],
    }));

    onSetOriginValues('plants');
    setPlants([...plantsSelected, ...newPlants]);
    setShowFirstStepAdd(false);
    setLoading(false);
  };

  // if we have the main CR in version more than 1 and we have last versions of plants
  const cantEditBecauseRequestFromSubCR = workflow?.version > 1 && workflow?.historicalPlantStatuses?.length > 0;
  const disabledBYPRRD = isFieldDisabledBYPRRD(campaignInfo, 'affectedVehicles.seriesPanel');

  const cantEdit = cantEditBecauseRequestFromSubCR || checkEnabled || disabledBYPRRD;

  const displayIsModelSeriesKnown = (value) => {
    const option = value ? 'SERIES' : 'PLANTS';
    setPlants([]);
    setSelectedAffectedVehicles(option);
  };

  const handleAccordeonClick = () => {
    setHelpArea(HELP_AREA.SERIES);

    if (cantEditBecauseRequestFromSubCR) setCantEditPopup(true);
  };

  const modelSeriesKnownTable = (
    <div className={styles.ColumnCodingTable}>
      <SeriesTable
        isSeries
        isEditable={!cantEdit}
        checkEnabled={cantEdit}
        modelSeries={modelSeries}
        // @ts-ignore
        selections={series as LimitationSerie[]}
        // @ts-ignore
        onRowsUpdate={setSeries}
        changedRows={onSetOriginValues}
        handleDeleteSeries={(serie) => handleDelete(serie, true)}
      />
    </div>
  );

  const plantsTable = (
    <div className={styles.ColumnCodingTable}>
      <SeriesTable
        isEditable={!cantEdit}
        checkEnabled={cantEdit}
        options={plantsOptions}
        selections={plantsSelected}
        // @ts-ignore
        onRowsUpdate={setPlants}
        handleDeletePlants={(wkzNumber) => handleDelete(wkzNumber, false)}
      />
    </div>
  );

  const modelSeriesButtons = (
    <div className={styles.ButtonsContentTable}>
      <SimpleButton
        id="add-series-btn"
        disabled={cantEdit}
        color="primary"
        buttonText={showFirstStepAdd ? 'X' : t('affectedVehicles.addSeries')}
        handleClick={() => handleNewRowSeries()} // event is not used
      />
      <IcoButtonSimple
        disabled={cantEdit || series.every(({ checked }) => !checked)}
        id="btn-delete-plan"
        icon="icon-muelleimer-loeschen"
        tooltipText={t('software.delete')}
        handleClick={() => handleDelete('', true)} // event is not used
      />
    </div>
  );

  const viaPlantsButtons = (
    <div className={styles.ButtonsContentTable}>
      <SimpleButton
        id="add-plant-btn"
        disabled={cantEdit}
        color="primary"
        buttonText={showFirstStepAdd ? 'X' : t(`affectedVehicles.${plantsSelected.length === 0 ? 'addPlants' : 'addPlant'}`)}
        handleClick={() => handleNewRowPlants()} // event is not used
      />
      <IcoButtonSimple
        disabled={cantEdit || plantsSelected.every(({ checked }) => !checked)}
        id="btn-delete-plan"
        icon="icon-muelleimer-loeschen"
        tooltipText={t('software.delete')}
        handleClick={() => handleDelete('', false)} // event is not used
      />
    </div>
  );

  const leftButton = {
    id: 'via-model-series',
    label: t('affectedVehicles.modelSeriesKnown'),
    checked: isSeriesSelected,
    disabled: isSeriesSelected && (series.length > 0 || (!isSeriesSelected && affectedVehicles.affectedPlants.length > 0)
      || isCenterViewer || disabledBYPRRD),
    disabledMsg: t('affectedVehicles.onlyPlantsErrorMessage'),
  };

  const rightButton = {
    id: 'via-plants',
    label: t('affectedVehicles.onlyPlants'),
    checked: !isSeriesSelected,
    disabled: campaignStatus !== statusCampaign.IN_CREATION || (!isSeriesSelected && plantsSelected.length > 0) || series.length > 0
      || isCenterViewer || disabledBYPRRD,
    disabledMsg: t('affectedVehicles.onlyPlantsErrorMessage'),
  };

  const confirmDialog = () => {
    if (dialogProps.plantsWithFiles?.length) {
      confirmDeletePlants(!!dialogProps.isPlantsOption);
    } else if (dialogProps.data) {
      checkPlantsFilesAndSave(dialogProps.data, dialogProps?.isPlantsDeletion);
    }
  };

  return (
    <>
      <AccordionPanel
        title={<LabelDisabledByPDRS campaignInfo={campaignInfo} label="affectedVehicles.seriesAndPlants" fieldName="affectedVehicles.seriesPanel" />}
        // {t('affectedVehicles.seriesAndPlants')}
        handleClick={handleAccordeonClick}
        isMandatory={!cantEditBecauseRequestFromSubCR}
        invalid={invalid}
        fieldPath={['affectedVehicles', 'affectedPlantsUpdatedOn']}
      >
        <div className={styles.VolumeContainer}>
          <div className={styles.Flex}>
            <div className={styles.ButtonsLeftContainer}>
              <EntryButtons
                id="button-group-via-model-series"
                leftButton={leftButton}
                rightButton={rightButton}
                onClickAction={displayIsModelSeriesKnown}
              />
              <div className={styles.ButtonsLeftContainer}>
                {(isSeriesSelected && !cantEditBecauseRequestFromSubCR)
                  && <InfoMsg msg={t('affectedVehicles.viaModelSeriesActive')} />}
              </div>
            </div>
            {isSeriesSelected ? modelSeriesButtons : viaPlantsButtons}
          </div>
          <div className={styles.Flex}>
            {(isSeriesSelected && !showFirstStepAdd) && modelSeriesKnownTable}
            {(!isSeriesSelected && !showFirstStepAdd) && plantsTable}
            {showFirstStepAdd && (
              <SeriesFisrtStepSelector
                checkEnabled={cantEdit}
                modelSeries={modelSeries}
                plants={plantsOptions.plants}
                onSelectedSeries={handleAddMultipleSeries}
                onSelectedPlants={handleAddMultiplePlants}
                isPlants={!isSeriesSelected}
              />
            )}
          </div>
        </div>
      </AccordionPanel>
      {dialogProps.open && (
        <ConfirmDialog
          close={() => setDialogProps({ open: false })}
          confirm={confirmDialog}
          title={dialogProps.plantsWithFiles?.length
            ? t('affectedVehicles.dialog.title')
            : t(`dialog.${dialogProps.isPlantsDeletion ? 'deletePlantRow' : 'deleteModelSeriesRow'}.title`)}
          message={dialogProps.plantsWithFiles?.length
            ? t('affectedVehicles.dialog.message', { x: mapPlantsWithfiles(dialogProps.plantsWithFiles) })
            : t('dialog.deleteModelSeriesRow.message')}
          closeBtnText={t('dialog.cancel')}
          confirmBtnText={t('dialog.confirm')}
        />
      )}
      {cantEditPopup && (
        <ConfirmDialog
          close={() => setCantEditPopup(false)}
          title={t('dialog.cantEditBecRequestFromSubCR.title')}
          message={t('dialog.cantEditBecRequestFromSubCR.message')}
          closeBtnText={t('dialog.close')}
        />
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  series: getCampaignVehicles(state).series || [],
  plantsSelected: getSelectedPlants(state) || [],
  affectedVehicles: getCampaignVehicles(state),
  headerPlants: getVolumeHeaderPlants(state) || [] as WKZNamePlant[],
  volumes: getCampaignVehiclesVolumes(state),
  originalValues: getOriginalValues(state),
  campaignStatus: getCampaignInfo(state).status,
  userRole: getCampaignInfo(state).authorization.kindOfPermission,
  workflow: getWorkflow(state),
  campaignInfo: getCampaignInfo(state),
});

const actionCreators = {
  setSeries: setAffectedVehiclesSeries,
  setOriginValues: setOriginalValues,
  setSelectedAffectedVehicles: setCampaignVehiclesSelectedAffectedVehicles,
  setInfo: setCampaignInfo,
  setPlants: setAffectedPlants,
  setHelpArea: setAreaHelp,
};
// @ts-ignore
export default connect(mapStateToProps, actionCreators)(SeriesPanel);
